import Cookies from "js-cookie";
import PropTypes from "prop-types";
import { createContext, useReducer } from "react";
import type { FC, ReactNode } from "react";

import { Id } from "@/types/common.types";
import { LeagueId } from "@/types/league.types";
import type { CurrentUser } from "@/types/user.types";

type State = {
  leagueId: Id | null;
  leagues: any;
  dashboardStats: any;
  organizationStats: any;
  onboardingData: any;
};

export type AuthContextValue = {
  logout: () => Promise<void>;
  getLeagueId: (id: LeagueId | null) => Promise<void>;
  getOnboardingData: (onboardingData: any) => Promise<void>;

  getDashboardStats: (dashboardStats: any) => Promise<void>;
  getDashboardOrganizationStats: (organizationStats: any) => Promise<void>;

  getLeaguesData: (data: any) => Promise<void>;
} & State;

type AuthProviderProps = {
  children: ReactNode;
};

type InitializeAction = {
  type: "INITIALIZE";
  payload: {
    isAuthenticated: boolean;
    user: CurrentUser | null;
  };
};

type RoleAction = {
  type: "ROLES";
  payload: {
    roles: any | null;
  };
};
type LeagueAction = {
  type: "LEAGUES";
  payload: {
    leagues: any | null;
  };
};

type LoginAction = {
  type: "LOGIN";
  payload: {
    user: CurrentUser;
  };
};

type LogoutAction = {
  type: "LOGOUT";
};

type RegisterAction = {
  type: "REGISTER";
  payload: {
    user: CurrentUser;
  };
};
type leagueAction = {
  type: "GET_LEAGUE_ID";
  payload: {
    id: number | null;
  };
};
type userPlatformRoleAction = {
  type: "GET_USER_PLATFORM_ROLE";
  payload: {
    role: string;
  };
};

type platformLanguageAction = {
  type: "GET_PLATFORM_LANGUAGE";
  payload: {
    languages: string;
  };
};

type onboardingDataAction = {
  type: "GET_ONBOARDING_DATA";
  payload: {
    onboardingData: any;
  };
};

type dashboardStatsAction = {
  type: "GET_DASHBOARD_STATS_DATA";
  payload: {
    dashboardStats: any;
  };
};
type dashboardOrganizationStatsAction = {
  type: "GET_DASHBOARD_ORGANIZATIONS_STATS";
  payload: {
    organizationStats: any;
  };
};

type sportAction = {
  type: "GET_SPORT_ID";
  payload: {
    id: number;
  };
};

type platformAction = {
  type: "GET_PLATFORM_ID";
  payload: {
    id: number | null;
  };
};
type platformRelationalAction = {
  type: "GET_PLATFORM_RELATIONAL_ID";
  payload: {
    id: number;
  };
};

type Action =
  | InitializeAction
  | LoginAction
  | LogoutAction
  | RegisterAction
  | leagueAction
  | userPlatformRoleAction
  | platformLanguageAction
  | dashboardStatsAction
  | onboardingDataAction
  | dashboardOrganizationStatsAction
  | sportAction
  | platformAction
  | platformRelationalAction
  | RoleAction
  | LeagueAction;
const parseLeagueId = (value: string | undefined): State["leagueId"] => {
  if (!value || value === "undefined" || value === "null" || value === "NaN") return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const initialState: State = {
  leagueId: parseLeagueId(Cookies.get("leagueId")),
  dashboardStats: null,
  organizationStats: null,
  leagues: [] as any,
  onboardingData: [] as any,
};

const handlers = {
  LEAGUES: (state: State, action: LeagueAction): State => {
    const { leagues } = action.payload;

    return {
      ...state,
      leagues: leagues,
    };
  },
  LOGOUT: (state: State): State => ({
    ...state,
    leagueId: null,
    dashboardStats: "",
    organizationStats: "",
    onboardingData: "",
  }),
  GET_LEAGUE_ID: (state: State, action: any): any => {
    const { id } = action.payload;

    return {
      ...state,
      leagueId: id,
    };
  },

  GET_USER_PLATFORM_ROLE: (state: State, action: any): any => {
    const { role } = action.payload;

    return {
      ...state,
      userPlatformRole: role,
    };
  },

  GET_PLATFORM_DATA: (state: State, action: any): any => {
    const { platformData } = action.payload;

    return {
      ...state,
      platformData,
    };
  },
  GET_ONBOARDING_DATA: (state: State, action: any): any => {
    const { onboardingData } = action.payload;

    return {
      ...state,
      onboardingData,
    };
  },
  GET_DASHBOARD_STATS_DATA: (state: State, action: any): any => {
    const { dashboardStats } = action.payload;

    return {
      ...state,
      dashboardStats,
    };
  },
  GET_DASHBOARD_ORGANIZATIONS_STATS: (state: State, action: any): any => {
    const { organizationStats } = action.payload;

    return {
      ...state,
      organizationStats,
    };
  },

  GET_PLATFORM_ID: (state: State, action: any): any => {
    const { id } = action.payload;

    return {
      ...state,
      platformId: id,
    };
  },
  GET_PLATFORM_RELATIONAL_ID: (state: State, action: any): any => {
    const { id } = action.payload;

    return {
      ...state,
      platformRelationalId: id,
    };
  },
};
const reducer = (state: State, action: Action): State =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextValue>({
  ...initialState,
  logout: () => Promise.resolve(),
  getLeagueId: () => Promise.resolve(),
  getLeaguesData: () => Promise.resolve(),
  getOnboardingData: () => Promise.resolve(),
  getDashboardStats: () => Promise.resolve(),
  getDashboardOrganizationStats: () => Promise.resolve(),
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialState);

  const logout = async (): Promise<void> => {
    Cookies.remove("user");
    Cookies.remove("leagueId");

    Cookies.remove("platformId");
    Cookies.remove("platformRelationalId");
    Cookies.remove("organizationId");
    Cookies.remove("lang");
    Cookies.remove("isSkipOnboarding");

    dispatch({ type: "LOGOUT" });
    dispatch({
      type: "GET_LEAGUE_ID",
      payload: {
        id: 0,
      },
    });

    dispatch({
      type: "GET_USER_PLATFORM_ROLE",
      payload: {
        role: "",
      },
    });

    dispatch({
      type: "GET_PLATFORM_ID",
      payload: {
        id: 0,
      },
    });
    dispatch({
      type: "GET_PLATFORM_RELATIONAL_ID",
      payload: {
        id: 0,
      },
    });
  };
  const getLeaguesData = async (data: any): Promise<void> => {
    dispatch({
      type: "LEAGUES",
      payload: {
        leagues: data,
      },
    });
  };

  const getLeagueId = async (id: LeagueId | null): Promise<void> => {
    if (id === null || id === undefined || Number.isNaN(id)) {
      Cookies.remove("leagueId");
    } else {
      Cookies.set("leagueId", String(id));
    }
    dispatch({
      type: "GET_LEAGUE_ID",
      payload: {
        id,
      },
    });
  };

  const getOnboardingData = async (onboardingData: any): Promise<void> => {
    dispatch({
      type: "GET_ONBOARDING_DATA",
      payload: {
        onboardingData,
      },
    });
  };

  const getDashboardStats = async (dashboardStats: any): Promise<void> => {
    dispatch({
      type: "GET_DASHBOARD_STATS_DATA",
      payload: {
        dashboardStats,
      },
    });
  };
  const getDashboardOrganizationStats = async (organizationStats: any): Promise<void> => {
    dispatch({
      type: "GET_DASHBOARD_ORGANIZATIONS_STATS",
      payload: {
        organizationStats,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        logout,
        getLeagueId,
        getLeaguesData,
        getDashboardStats,
        getDashboardOrganizationStats,
        getOnboardingData,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

AuthProvider.propTypes = {
  children: PropTypes.node as PropTypes.Validator<ReactNode>,
};

export default AuthContext;
